/**
 * @copyright
 * Copyright (c) 2025 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */
import ApiCaller from "~/api/ApiCaller";
import { ResellerDetails } from "~/features/auth/types";
import {
  ComplianceDocumentApiSubType,
  GetComplianceDetailsResponse,
  GetComplianceReviewFailResponseData,
  GetComplianceReviewInfoResponseData,
  GetComplianceReviewSuccessResponseData,
  GetComplianceStatusResponseData,
  GetPaymentInfoResponseData,
  GetValidLocationsResponse,
  GetWorldPayFieldMappingsResponseData,
  PostComplianceDetailsPayload,
  PostComplianceDetailsResponse,
  PostDeleteDocumentPayload,
  PostFinalizeComplianceRequestBody,
  PostPaymentInfoPayload,
  PostPaymentInfoResponseData,
  PostSaveResellerInfoPayload,
  PostSaveResellerInfoResponseData,
  PostUpdateComplianceRequestBody,
  PostUploadComplianceDocumentResponseData,
} from "~/features/myAccount/types";
import { WebserviceResponse } from "~/types";

const MyAccountApi = {
  getResellerDetails: () => ApiCaller.get<WebserviceResponse<ResellerDetails>>("/v1/reseller/details"),

  saveResellerInfo: (data: PostSaveResellerInfoPayload) =>
    ApiCaller.post<PostSaveResellerInfoResponseData>("/v1/reseller/update", data),

  getComplianceStatus: () => ApiCaller.get<GetComplianceStatusResponseData>("/v1/resellercompliance/resellerstatus"),

  getPaymentInfo: () => ApiCaller.get<GetPaymentInfoResponseData>("/v1/reseller/paymentinfo"),

  getWorldPayFieldMappings: () => ApiCaller.get<GetWorldPayFieldMappingsResponseData>("/v1/worldpay/fieldmappings"),

  postPaymentInfoUpdate: (data: PostPaymentInfoPayload) =>
    ApiCaller.post<PostPaymentInfoResponseData>("/v1/reseller/paymentinfoupdate", data),

  postPaymentInfoCreate: (data: PostPaymentInfoPayload) =>
    ApiCaller.post<PostPaymentInfoResponseData>("/v1/reseller/paymentinfocreate", data),

  getComplianceReview: () =>
    ApiCaller.get<GetComplianceReviewSuccessResponseData | GetComplianceReviewFailResponseData>(
      "/v1/resellercompliance/latestreview",
    ),

  getComplianceDocuments: () => ApiCaller.get<ComplianceDocumentApiSubType[]>("/v1/document/pendingdocuments"),

  getComplianceReviewInfo: (reviewId: number) =>
    ApiCaller.get<GetComplianceReviewInfoResponseData>(`/v1/resellercompliance/reviewinfo?reviewid=${reviewId}`),

  postUploadDocument: (file: string, fileName: string, documentType: string, note?: string) =>
    ApiCaller.post<PostUploadComplianceDocumentResponseData>("/v1/document/upload", {
      fileName: fileName,
      file: file,
      documentType: documentType,
      note: note,
    }),

  postStartReview: () => ApiCaller.post("/v1/resellercompliance/documentsready"),

  postDeleteDocument: (data: PostDeleteDocumentPayload) =>
    ApiCaller.post("/v1/resellercompliance/deleteresellerdocument", data),

  getComplianceDetails: () => ApiCaller.get<GetComplianceDetailsResponse>("/v1/resellercompliance/recentdetails"),

  postComplianceDetails: (payload: PostComplianceDetailsPayload) =>
    ApiCaller.post<PostComplianceDetailsResponse>("/v1/resellercompliance/create", payload),

  postUpdateComplianceReview: (body: PostUpdateComplianceRequestBody) =>
    ApiCaller.post<PostComplianceDetailsResponse>("/v1/resellercompliance/update", body),

  postFinalizeComplianceReview: (body: PostFinalizeComplianceRequestBody) =>
    ApiCaller.post<PostComplianceDetailsResponse>("/v1/resellercompliance/finalize", body),

  getValidLocations: () => ApiCaller.get<GetValidLocationsResponse>("/v1/resellercompliance/validlocations"),
};

export default MyAccountApi;
